import { useState, useMemo, useRef, useEffect } from 'react'
import { ChevronDownIcon } from 'lucide-react'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import fieldsOfStudy from '@/data/fieldsofstudy'

interface Field {
  name: string
  studyName: string
  educationalArea: string
}

const fields: Field[] = [
  { name: 'Se alle studier', studyName: '', educationalArea: '' },
  {
    name: 'Fysioterapi',
    studyName: 'Fysioterapi',
    educationalArea: 'HELSEFAG - FYSIO',
  },
  {
    name: 'Grunnskolelærer 1-7',
    studyName: 'Grunnskolelærer',
    educationalArea: 'LÆRER - GLU1-7',
  },
  {
    name: 'Grunnskolelærer 5-10',
    studyName: 'Grunnskolelærer',
    educationalArea: 'LÆRER - GLU5-10',
  },
  {
    name: 'Idrett',
    studyName: 'Idrett',
    educationalArea: 'IDRETT',
  },
  {
    name: 'IT/Informatikk',
    studyName: 'Informatikk',
    educationalArea: 'INFOTEKN',
  },
  {
    name: 'Medisin',
    studyName: 'Medisin',
    educationalArea: 'HELSEFAG - MEDISIN',
  },
  {
    name: 'Odontologi',
    studyName: 'Odontologi',
    educationalArea: 'HELSEFAG - ODONT',
  },
  {
    name: 'Psykologi',
    studyName: 'Psykologi',
    educationalArea: 'SAMFUNN',
  },
  {
    name: 'Rettsvitenskap/Jus',
    studyName: 'Rettsvitenskap',
    educationalArea: 'JUS',
  },
  {
    name: 'Siviløkonom',
    studyName: 'Siviløkonom',
    educationalArea: 'ØKADM',
  },
  {
    name: 'Sykepleie',
    studyName: 'Sykepleie',
    educationalArea: 'HELSEFAG - SYKEPL',
  },
  {
    name: 'Veterinær',
    studyName: 'Veterinær',
    educationalArea: 'HELSEFAG - VETERIN',
  },
]

const StudyProgramSelector = () => {
  const [selectedField, setSelectedField] = useState<Field | null>(null)
  const [selectedStudy, setSelectedStudy] = useState<string>('')
  const [fieldDropdownOpen, setFieldDropdownOpen] = useState(false)
  const [studyDropdownOpen, setStudyDropdownOpen] = useState(false)
  const [studySearch, setStudySearch] = useState('')

  const fieldDropdownRef = useRef<HTMLDivElement>(null)
  const studyDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        fieldDropdownRef.current &&
        !fieldDropdownRef.current.contains(event.target as Node)
      ) {
        setFieldDropdownOpen(false)
      }
      if (
        studyDropdownRef.current &&
        !studyDropdownRef.current.contains(event.target as Node)
      ) {
        setStudyDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [setFieldDropdownOpen, setStudyDropdownOpen])

  const matchingStudies = useMemo(() => {
    if (!selectedField) {
      return []
    }
    if (selectedField.name === 'Se alle studier') {
      return fieldsOfStudy.sort((a, b) =>
        a.Studienavn.localeCompare(b.Studienavn)
      )
    }
    return fieldsOfStudy
      .filter((study) => {
        const areaMatch = study.Utdanningsområde.toLowerCase().includes(
          selectedField.educationalArea.toLowerCase()
        )
        const nameMatch =
          selectedField.studyName === '' ||
          study.Studienavn.toLowerCase().includes(
            selectedField.studyName.toLowerCase()
          )
        return areaMatch && nameMatch
      })
      .sort((a, b) => a.Studienavn.localeCompare(b.Studienavn))
  }, [selectedField])

  const filteredStudies = useMemo(() => {
    return matchingStudies.filter((study) =>
      study.Studienavn.toLowerCase().includes(studySearch.toLowerCase())
    )
  }, [matchingStudies, studySearch])

  const selectedStudyData = useMemo(() => {
    return fieldsOfStudy.find((study) => study.Studiekode === selectedStudy)
  }, [selectedStudy])

  const handleFieldChange = (field: Field) => {
    setSelectedField(field)
    setSelectedStudy('')
    setFieldDropdownOpen(false)
    if (matchingStudies.length === 1) {
      setSelectedStudy(matchingStudies[0].Studiekode)
    }
  }

  const handleStudyChange = (studyCode: string) => {
    setSelectedStudy(studyCode)
    setStudyDropdownOpen(false)
    setStudySearch('')
  }

  return (
    <div className="space-y-4">
      <label className="block text-base font-bold text-purple-800 mb-3">
        Se poenggrenser på ulike studier
      </label>

      <div className="space-y-4">
        <div className="relative" ref={fieldDropdownRef}>
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setFieldDropdownOpen(!fieldDropdownOpen)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="w-full flex items-center justify-between border-warm-gray-600 shadow-[0_2px_4px_0_rgba(0,0,0,0.1)] rounded-sm px-3 py-2 text-sm border bg-white"
            data-testid="field-selector"
          >
            <span>{selectedField?.name || 'Velg ditt studieønske'}</span>
            <ChevronDownIcon className="h-4 w-4 opacity-50" />
          </button>

          {fieldDropdownOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
              <div className="max-h-[200px] overflow-y-auto">
                {fields.map((field) => (
                  <div
                    key={field.name}
                    className="cursor-pointer px-3 py-1.5 text-xs hover:bg-purple-100"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleFieldChange(field)
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleFieldChange(field)
                    }}
                    data-testid={`field-option-${field.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    {field.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {selectedField && matchingStudies.length > 0 && (
          <div className="relative" ref={studyDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Studier
            </label>
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!studyDropdownOpen) {
                  setStudySearch('')
                }
                setStudyDropdownOpen(!studyDropdownOpen)
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full flex items-center justify-between border-warm-gray-600 shadow-[0_2px_4px_0_rgba(0,0,0,0.1)] rounded-sm px-3 py-2 text-sm border bg-white"
              data-testid="study-selector"
            >
              <span>
                {selectedStudy
                  ? `${selectedStudyData?.Studienavn}, ${selectedStudyData?.Studiested}`
                  : 'Velg studie'}
              </span>
              <ChevronDownIcon className="h-4 w-4 opacity-50" />
            </button>

            {studyDropdownOpen && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                <Command className="overflow-hidden" shouldFilter={false}>
                  <CommandInput
                    placeholder="Søk studie..."
                    value={studySearch}
                    onValueChange={setStudySearch}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <CommandList className="max-h-[200px]">
                    <CommandGroup>
                      {filteredStudies.map((study) => (
                        <CommandItem
                          key={study.Studiekode}
                          className="cursor-pointer data-[selected=true]:bg-purple-100 text-xs py-1.5"
                          onSelect={() => {
                            handleStudyChange(study.Studiekode)
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleStudyChange(study.Studiekode)
                          }}
                          data-testid={`study-option-${study.Studiekode}`}
                        >
                          {study.Studienavn}, {study.Studiested}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedStudyData && (
        <div
          className="mt-6 bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
          data-testid="selected-study-info"
        >
          <h3
            className="text-lg font-semibold text-gray-800 mb-2"
            data-testid="selected-study-name"
          >
            {selectedStudyData.Studienavn}
          </h3>
          <div className="text-sm text-gray-600 mb-3">
            <span data-testid="selected-study-location">
              {selectedStudyData.Lærested} - {selectedStudyData.Studiested}
            </span>
          </div>

          <div
            className="bg-blue-50 p-3 rounded-lg"
            data-testid="study-requirements"
          >
            <div className="text-sm font-medium text-gray-700 mb-2">
              Krav ved forrige opptak (2025):
            </div>
            {selectedStudyData.ORD > 1 ? (
              <div className="flex gap-6">
                <div>
                  <div className="text-xs text-gray-500">Ordinær kvote</div>
                  <div
                    className="font-bold text-blue-600"
                    data-testid="selected-ord-score"
                  >
                    {selectedStudyData.ORD} poeng
                  </div>
                </div>
                {selectedStudyData.ORDF && (
                  <div>
                    <div className="text-xs text-gray-500">
                      Førstegangskvote
                    </div>
                    <div
                      className="font-bold text-green-600"
                      data-testid="selected-ordf-score"
                    >
                      {selectedStudyData.ORDF} poeng
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="font-bold text-gray-600"
                data-testid="no-requirements"
              >
                Ingen poenggrense
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default StudyProgramSelector
